import styled from 'styled-components';
import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import { CircularProgress } from '@mui/material';
import { useAssetData } from 'shared/hooks/useAssetData/useAssetData';
import PanelError from 'shared/components/PanelError';
import { space } from '@elementary/common-ui/style/utils';
import CatalogTabs from './CatalogTabs';
import DetailsSection from './DetailsSection';
import AssetHeader from './AssetHeader';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  gap: ${space('sm')};
`;

const StickySection = styled(FlexColumn)<{ $sticky: boolean }>`
  background: ${({ theme }) => theme.palette.surface.base};
  gap: ${space('xxl')};
  padding: ${space('xxl', 0, 'sm', 0)};
  margin-top: -${space('xxl')};

  ${({ $sticky }) =>
    $sticky &&
    `
    position: sticky;
    top: -24px;
    z-index: 4;
  `}
`;

type Props = {
  id?: string;
  showActions?: boolean;
  enableEditDependencies?: boolean;
  isTest?: boolean;
};

const AssetDetails = ({
  id,
  showActions = false,
  enableEditDependencies,
  isTest,
}: Props) => {
  const {
    assetData,
    testData,
    isLoading: isAssetDataLoading,
    isError: isAssetDataError,
    error,
  } = useAssetData({ id, isTest });

  if (isAssetDataLoading || isAssetDataError) {
    return (
      <FlexCenter
        sx={{ height: '100%', padding: space(0, 'xxl'), width: '100%' }}
      >
        {isAssetDataError ? (
          <PanelError
            error={error as Error}
            errorName={'ApiError'}
            page={'AssetDetails'}
          />
        ) : (
          <CircularProgress />
        )}
      </FlexCenter>
    );
  }

  return (
    <Wrapper>
      <StickySection $sticky={!!showActions}>
        <AssetHeader
          asset={assetData}
          testData={testData}
          showActions={showActions}
          clickableTitle
        />
      </StickySection>

      <DetailsSection
        asset={assetData}
        testData={testData}
        lastDataModificationTime={assetData?.lastDataModificationTime}
      />

      <CatalogTabs
        asset={assetData}
        testData={testData}
        enableEditDependencies={enableEditDependencies}
      />
    </Wrapper>
  );
};

export default AssetDetails;
